import { isOk } from "@/common/common";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { customFieldsApiClient } from "./customFieldsApiClient";
import {
  EntityDataImportField,
  EntityDataTarget,
  fieldKey,
  fieldLabel,
  fieldSection,
} from "./entityDataImportDomain";
import {
  blankUnmappedCells,
  cellEditor,
  errorCount,
  ImportFieldOption,
  resultRows,
  SpreadsheetImportGrid,
  SpreadsheetImportIssue,
  SpreadsheetImportResult,
  SpreadsheetImportRow,
} from "./spreadsheetImportDomain";
import {
  openSpreadsheetImport,
  prepSpreadsheetImportFlow,
  setupOf,
  SpreadsheetImportFlow,
  SpreadsheetImportFlowProps,
  SpreadsheetImportState,
} from "./spreadsheetImportFlow";

export const openFieldHistoryImport = (
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
): DCEvent[] =>
  openSpreadsheetImport(
    fieldHistoryImportFlow,
    accountId,
    files,
    afterImport,
    customFieldsApiClient.readFieldHistoryImport,
    ({ rows, columns, target, ...setup }) => ({
      setup,
      rows,
      columns: columns.map((column) => suggestedColumn(column, target)),
    }),
  );

export type FieldHistoryImportViewProps = SpreadsheetImportFlowProps<FieldHistoryImportSetup> & {
  missingRequired: TMessage[];
  severalFields: boolean;
  readyCompanies?: number;
  readyChanges?: number;
  canImport: boolean;
  result?: SpreadsheetImportResult;
};

export const prepFieldHistoryImportView = (
  state: FieldHistoryImportState | undefined,
  dispatch: EventDispatcher,
): FieldHistoryImportViewProps | undefined => {
  if (!state) {
    return undefined;
  }
  const validation = isOk(state.validation) ? state.validation.data : undefined;
  const missing = missingRequired(state);
  const severalFields = mappedFields(state).length > 1;
  return {
    ...prepSpreadsheetImportFlow(
      fieldHistoryImportFlow,
      state,
      setupOf(state, noSetup),
      gridOf(state),
      validation?.issues ?? [],
      dispatch,
    ),
    missingRequired: missing,
    severalFields,
    readyCompanies: validation?.companies,
    readyChanges: validation?.changes,
    canImport:
      isOk(state.setupResult) &&
      missing.length === 0 &&
      !severalFields &&
      (validation?.companies ?? 0) > 0 &&
      state.resultRevision === state.revision,
    result: isOk(state.importResult) ? importResultOf(state, state.importResult.data) : undefined,
  };
};

export const importRequest = (state: FieldHistoryImportState): FieldHistoryImportRequest => {
  const target = mappedFields(state)[0]?.target;
  const valueKey = target && fieldKey(target);
  const columns = state.columns.map((key): FieldHistoryColumn | null => {
    if (isPeriodColumn(key)) {
      return key;
    }
    return key !== undefined && key === valueKey ? "VALUE" : null;
  });
  return {
    rows: blankUnmappedCells(state.rows, (index) => columns[index] !== null),
    target,
    columns,
  };
};

export const missingRequired = (state: FieldHistoryImportState): TMessage[] => [
  ...(mappedFields(state).length === 0 ? [valueLabel] : []),
  ...(state.columns.includes("DATE_FROM") ? [] : [periodLabels.DATE_FROM]),
];

export const gridOf = (state: FieldHistoryImportState): SpreadsheetImportGrid => ({
  headers: setupOf(state, noSetup).headers,
  rows: state.rows,
  columns: state.columns,
  fieldsByKey: new Map(
    [
      periodOption("DATE_FROM"),
      periodOption("DATE_TO"),
      ...setupOf(state, noSetup).fields.map(fieldOption),
    ].map((option) => [option.key, option]),
  ),
  errorsOnly: state.errorsOnly,
  selectedOnly: state.selectedOnly,
});

const importResultOf = (
  state: FieldHistoryImportState,
  response: FieldHistoryImportResponse,
): SpreadsheetImportResult => ({
  counts: [
    {
      key: "companies",
      label: { key: "field-history-import.result-companies" },
      value: response.companies,
    },
    {
      key: "changes",
      label: { key: "field-history-import.result-changes" },
      value: response.changes,
    },
    {
      key: "unchanged",
      label: { key: "entity-data.import-unchanged" },
      value: response.unchanged,
    },
    {
      key: "rejected",
      label: { key: "entity-data.role-import-not-imported" },
      value: errorCount(response.issues),
    },
  ],
  nothingImported: response.companies === 0,
  rows: resultRows(gridOf(state), response.issues),
});

const mappedFields = (state: FieldHistoryImportState): EntityDataImportField[] => {
  const keys = new Set(state.columns.filter((key) => key !== undefined && !isPeriodColumn(key)));
  return setupOf(state, noSetup).fields.filter((field) => keys.has(fieldKey(field.target)));
};

export type FieldHistoryColumn = "VALUE" | "DATE_FROM" | "DATE_TO";

type PeriodColumn = Exclude<FieldHistoryColumn, "VALUE">;

export type FieldHistoryImportSheet = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  fields: EntityDataImportField[];
  target?: EntityDataTarget;
  columns: (FieldHistoryColumn | null)[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

export type FieldHistoryImportRequest = {
  rows: SpreadsheetImportRow[];
  target?: EntityDataTarget;
  columns: (FieldHistoryColumn | null)[];
};

export type FieldHistoryImportResponse = {
  companies: number;
  changes: number;
  unchanged: number;
  issues: SpreadsheetImportIssue[];
};

type FieldHistoryImportSetup = Omit<FieldHistoryImportSheet, "rows" | "columns" | "target">;

export type FieldHistoryImportState = SpreadsheetImportState<
  FieldHistoryImportSetup,
  FieldHistoryImportResponse
>;

const fieldHistoryImportFlow: SpreadsheetImportFlow<
  FieldHistoryImportSetup,
  FieldHistoryImportRequest,
  FieldHistoryImportResponse
> = {
  set: (change) => ({ type: "update-store", data: { fieldHistoryImport: change } }),
  stateOf: (root) => root.fieldHistoryImport,
  initial: {},
  requestOf: (state) => importRequest(state),
  validate: customFieldsApiClient.validateFieldHistoryImport,
  import: customFieldsApiClient.importFieldHistory,
};

const suggestedColumn = (
  column: FieldHistoryColumn | null,
  target: EntityDataTarget | undefined,
): string | undefined => (column === "VALUE" ? target && fieldKey(target) : (column ?? undefined));

const noSetup: FieldHistoryImportSetup = {
  headers: [],
  fields: [],
  countryCodes: [],
  fieldCountryCodes: [],
};

const valueLabel: TMessage = { key: "field-history-import.value" };

const periodLabels: Record<PeriodColumn, TMessage> = {
  DATE_FROM: { key: "field-history-import.date-from" },
  DATE_TO: { key: "field-history-import.date-to" },
};

const isPeriodColumn = (key: string | undefined): key is PeriodColumn =>
  key === "DATE_FROM" || key === "DATE_TO";

const periodOption = (column: PeriodColumn): ImportFieldOption => ({
  key: column,
  label: periodLabels[column],
  section: { key: "field-history-import.period-section" },
  editor: { type: "date" },
});

const fieldOption = (field: EntityDataImportField): ImportFieldOption => ({
  key: fieldKey(field.target),
  label: fieldLabel(field),
  section: fieldSection(field),
  editor: cellEditor(field.dataType, field.options),
});
